import React, { useState, useEffect, useCallback } from "react";
import { getDuplicates, getDuplicateStats, resolveDuplicate, deleteDuplicate, getCampaigns, getSources } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  Copy,
  MoreHorizontal,
  Search,
  Filter,
  RefreshCw,
  Trash2,
  XCircle,
  Download,
  FileWarning,
  Database,
  Phone,
  Mail,
  Calendar,
  Eye,
  Upload,
  Loader2,
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import api from "../lib/api";

// Add bulk delete API function
const bulkDeleteDuplicates = (ids) => api.delete("/duplicates/bulk", { data: { ids } });

export const DuplicatesPage = () => {
  const [duplicates, setDuplicates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [sources, setSources] = useState([]);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    search: "",
    duplicateType: "all",
    duplicateField: "all",
    resolution: "all",
    campaignId: "all",
    sourceId: "all",
    dateFrom: "",
    dateTo: "",
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  // Detail modal
  const [selectedDuplicate, setSelectedDuplicate] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  
  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [duplicateToDelete, setDuplicateToDelete] = useState(null);

  const fetchStats = async () => {
    try {
      const response = await getDuplicateStats();
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchDuplicates = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        pageSize,
      };
      
      // Only add filters that have real values (not "all")
      if (filters.search) params.search = filters.search;
      if (filters.duplicateType && filters.duplicateType !== "all") params.duplicateType = filters.duplicateType;
      if (filters.duplicateField && filters.duplicateField !== "all") params.duplicateField = filters.duplicateField;
      if (filters.resolution && filters.resolution !== "all") params.resolution = filters.resolution;
      if (filters.campaignId && filters.campaignId !== "all") params.campaignId = filters.campaignId;
      if (filters.sourceId && filters.sourceId !== "all") params.sourceId = filters.sourceId;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      
      const response = await getDuplicates(params);
      setDuplicates(response.data.data);
      setTotalPages(response.data.totalPages);
      setTotal(response.data.total);
      setSelectedIds([]); // Reset selection on new fetch
    } catch (error) {
      toast.error("Erreur lors du chargement des doublons");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  const fetchCampaignsAndSources = async () => {
    try {
      const [campaignsRes, sourcesRes] = await Promise.all([
        getCampaigns(),
        getSources(),
      ]);
      setCampaigns(campaignsRes.data);
      setSources(sourcesRes.data);
    } catch (error) {
      console.error("Error fetching campaigns/sources:", error);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchCampaignsAndSources();
  }, []);

  useEffect(() => {
    fetchDuplicates();
  }, [fetchDuplicates]);

  const handleResolve = async (id, resolution) => {
    try {
      await resolveDuplicate(id, resolution);
      toast.success(`Doublon ${resolution === "skip" ? "ignoré" : resolution === "import" ? "importé" : "mis à jour"}`);
      fetchDuplicates();
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors de la résolution");
    }
  };

  const handleDelete = async () => {
    if (!duplicateToDelete) return;
    try {
      await deleteDuplicate(duplicateToDelete);
      toast.success("Doublon supprimé de l'historique");
      setDeleteDialogOpen(false);
      setDuplicateToDelete(null);
      fetchDuplicates();
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setBulkDeleting(true);
    try {
      // Delete one by one since we don't have a bulk endpoint
      let deleted = 0;
      for (const id of selectedIds) {
        try {
          await deleteDuplicate(id);
          deleted++;
        } catch (e) {
          console.error(`Failed to delete ${id}:`, e);
        }
      }
      toast.success(`${deleted} doublon(s) supprimé(s)`);
      setBulkDeleteOpen(false);
      setSelectedIds([]);
      fetchDuplicates();
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors de la suppression en masse");
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchDuplicates();
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      duplicateType: "all",
      duplicateField: "all",
      resolution: "all",
      campaignId: "all",
      sourceId: "all",
      dateFrom: "",
      dateTo: "",
    });
    setPage(1);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === duplicates.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(duplicates.map(d => d.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const getResolutionBadge = (resolution) => {
    switch (resolution) {
      case "pending":
        return <Badge variant="outline" className="text-yellow-600 border-yellow-600">En attente</Badge>;
      case "skipped":
        return <Badge variant="secondary">Ignoré</Badge>;
      case "imported":
        return <Badge className="bg-green-500">Importé</Badge>;
      case "updated":
        return <Badge className="bg-blue-500">Mis à jour</Badge>;
      default:
        return <Badge variant="outline">{resolution}</Badge>;
    }
  };

  const getDuplicateTypeBadge = (type) => {
    if (type === "in_file") {
      return (
        <Badge variant="outline" className="text-orange-600 border-orange-600">
          <FileWarning className="h-3 w-3 mr-1" />
          Fichier
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-purple-600 border-purple-600">
        <Database className="h-3 w-3 mr-1" />
        Base
      </Badge>
    );
  };

  const getDuplicateFieldIcon = (field) => {
    if (field === "phone") {
      return <Phone className="h-4 w-4 text-muted-foreground" />;
    }
    return <Mail className="h-4 w-4 text-muted-foreground" />;
  };

  const activeFiltersCount = Object.entries(filters).filter(([key, v]) => {
    if (key === "search") return v !== "";
    if (key === "dateFrom" || key === "dateTo") return v !== "";
    return v !== "all" && v !== "";
  }).length;

  if (loading && duplicates.length === 0) {
    return (
      <div className="space-y-6" data-testid="duplicates-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="duplicates-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Gestion des doublons</h1>
          <p className="text-muted-foreground mt-1">
            Historique des doublons détectés lors des imports
          </p>
        </div>
        <div className="flex gap-2">
          {selectedIds.length > 0 && (
            <Button 
              variant="destructive" 
              onClick={() => setBulkDeleteOpen(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Supprimer ({selectedIds.length})
            </Button>
          )}
          <Button variant="outline" onClick={() => { fetchDuplicates(); fetchStats(); }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total doublons</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-yellow-600">En attente</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{stats?.pending || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Ignorés</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.skipped || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-green-600">Importés/MAJ</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {(stats?.imported || 0) + (stats?.updated || 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par nom, email, téléphone..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="pl-9"
                />
              </div>
              <Button type="submit" variant="secondary">
                Rechercher
              </Button>
            </form>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={activeFiltersCount > 0 ? "border-primary" : ""}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filtres
              {activeFiltersCount > 0 && (
                <Badge className="ml-2" variant="secondary">{activeFiltersCount}</Badge>
              )}
            </Button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4 pt-4 border-t">
              <div className="space-y-2">
                <Label>Type de doublon</Label>
                <Select
                  value={filters.duplicateType}
                  onValueChange={(v) => setFilters(prev => ({ ...prev, duplicateType: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Tous les types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les types</SelectItem>
                    <SelectItem value="in_file">Dans le fichier</SelectItem>
                    <SelectItem value="in_database">En base de données</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Champ dupliqué</Label>
                <Select
                  value={filters.duplicateField}
                  onValueChange={(v) => setFilters(prev => ({ ...prev, duplicateField: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Tous les champs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les champs</SelectItem>
                    <SelectItem value="phone">Téléphone</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Résolution</Label>
                <Select
                  value={filters.resolution}
                  onValueChange={(v) => setFilters(prev => ({ ...prev, resolution: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Toutes les résolutions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes</SelectItem>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="skipped">Ignoré</SelectItem>
                    <SelectItem value="imported">Importé</SelectItem>
                    <SelectItem value="updated">Mis à jour</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Campagne</Label>
                <Select
                  value={filters.campaignId}
                  onValueChange={(v) => setFilters(prev => ({ ...prev, campaignId: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Toutes les campagnes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les campagnes</SelectItem>
                    {campaigns.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Source</Label>
                <Select
                  value={filters.sourceId}
                  onValueChange={(v) => setFilters(prev => ({ ...prev, sourceId: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Toutes les sources" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les sources</SelectItem>
                    {sources.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Date du</Label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label>Date au</Label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                />
              </div>

              <div className="flex items-end">
                <Button variant="ghost" onClick={clearFilters} className="w-full">
                  Effacer les filtres
                </Button>
              </div>
            </div>
          )}
        </CardHeader>

        <CardContent>
          {total > 0 && (
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">
                {total} doublon{total !== 1 ? "s" : ""} trouvé{total !== 1 ? "s" : ""}
                {selectedIds.length > 0 && (
                  <span className="ml-2 text-primary font-medium">
                    ({selectedIds.length} sélectionné{selectedIds.length !== 1 ? "s" : ""})
                  </span>
                )}
              </p>
            </div>
          )}

          {duplicates.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Copy className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Aucun doublon trouvé</p>
              <p className="text-sm mt-2">Les doublons détectés lors des imports apparaîtront ici</p>
            </div>
          ) : (
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedIds.length === duplicates.length && duplicates.length > 0}
                        onCheckedChange={toggleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Champ</TableHead>
                    <TableHead>Campagne</TableHead>
                    <TableHead>Date import</TableHead>
                    <TableHead>Résolution</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {duplicates.map((dup) => (
                    <TableRow 
                      key={dup.id}
                      className={selectedIds.includes(dup.id) ? "bg-muted/50" : ""}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.includes(dup.id)}
                          onCheckedChange={() => toggleSelect(dup.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">
                            {dup.firstName} {dup.lastName}
                          </p>
                          <p className="text-sm text-muted-foreground">{dup.email || dup.phone}</p>
                        </div>
                      </TableCell>
                      <TableCell>{getDuplicateTypeBadge(dup.duplicateType)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getDuplicateFieldIcon(dup.duplicateField)}
                          <span className="capitalize">{dup.duplicateField === "phone" ? "Tél" : "Email"}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {dup.campaignName && (
                          <Badge variant="outline">{dup.campaignName}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          {dup.importDate && format(new Date(dup.importDate), "dd/MM/yyyy", { locale: fr })}
                        </div>
                      </TableCell>
                      <TableCell>{getResolutionBadge(dup.resolution)}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => { setSelectedDuplicate(dup); setDetailOpen(true); }}>
                              <Eye className="h-4 w-4 mr-2" />
                              Voir détails
                            </DropdownMenuItem>
                            {dup.resolution === "pending" && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleResolve(dup.id, "skip")}>
                                  <XCircle className="h-4 w-4 mr-2" />
                                  Ignorer
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleResolve(dup.id, "import")}>
                                  <Download className="h-4 w-4 mr-2" />
                                  Importer quand même
                                </DropdownMenuItem>
                                {dup.existingLeadId && (
                                  <DropdownMenuItem onClick={() => handleResolve(dup.id, "update")}>
                                    <Upload className="h-4 w-4 mr-2" />
                                    Mettre à jour existant
                                  </DropdownMenuItem>
                                )}
                              </>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => {
                                setDuplicateToDelete(dup.id);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-2">
                <Label>Afficher</Label>
                <Select
                  value={pageSize.toString()}
                  onValueChange={(v) => { setPageSize(parseInt(v)); setPage(1); }}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Précédent
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {page} sur {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Suivant
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Détails du doublon</DialogTitle>
          </DialogHeader>
          {selectedDuplicate && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Nom</Label>
                  <p className="font-medium">{selectedDuplicate.firstName} {selectedDuplicate.lastName}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Résolution</Label>
                  <div className="mt-1">{getResolutionBadge(selectedDuplicate.resolution)}</div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p>{selectedDuplicate.email || "-"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Téléphone</Label>
                  <p>{selectedDuplicate.phone || "-"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Type de doublon</Label>
                  <div className="mt-1">{getDuplicateTypeBadge(selectedDuplicate.duplicateType)}</div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Champ dupliqué</Label>
                  <p className="capitalize">{selectedDuplicate.duplicateField === "phone" ? "Téléphone" : "Email"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Campagne</Label>
                  <p>{selectedDuplicate.campaignName || "-"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Source</Label>
                  <p>{selectedDuplicate.sourceName || "-"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Date d'import</Label>
                  <p>{selectedDuplicate.importDate && format(new Date(selectedDuplicate.importDate), "dd/MM/yyyy HH:mm", { locale: fr })}</p>
                </div>
                {selectedDuplicate.existingLeadName && (
                  <div>
                    <Label className="text-muted-foreground">Lead existant</Label>
                    <p>{selectedDuplicate.existingLeadName}</p>
                  </div>
                )}
              </div>

              {selectedDuplicate.csvData && Object.keys(selectedDuplicate.csvData).length > 0 && (
                <div>
                  <Label className="text-muted-foreground">Données CSV</Label>
                  <div className="mt-2 p-3 bg-muted rounded-md text-sm">
                    <pre className="whitespace-pre-wrap">
                      {JSON.stringify(selectedDuplicate.csvData, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailOpen(false)}>
              Fermer
            </Button>
            {selectedDuplicate?.resolution === "pending" && (
              <>
                <Button variant="secondary" onClick={() => { handleResolve(selectedDuplicate.id, "skip"); setDetailOpen(false); }}>
                  Ignorer
                </Button>
                <Button onClick={() => { handleResolve(selectedDuplicate.id, "import"); setDetailOpen(false); }}>
                  Importer
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce doublon ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action supprimera uniquement l'entrée de l'historique des doublons. 
              Le lead existant (s'il y en a un) ne sera pas affecté.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Dialog */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer {selectedIds.length} doublon(s) ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action supprimera les {selectedIds.length} doublons sélectionnés de l'historique.
              Les leads existants ne seront pas affectés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {bulkDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>Supprimer {selectedIds.length} doublon(s)</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
